// TODO: После доработки UI компонентов Input, Checkbox и других - заменить MUI компоненты на наши кастомные
// и применить их в этой форме подтверждения кода

import React, { useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid, Stack, Typography } from "@mui/material";
import ReactCodeInput from "react-code-input";

import { useAppContext } from "../../../../AppContext";
import { VerifyCodeTypes } from "../../types";
import { RetryCodeLinkButton } from "./RetryCodeLinkButton";
import { Button } from "../../../../ui/Button/Button";
import { VerifyCodeSchema } from "./schemas/VerifyCodeSchema";
import { ROUTES } from "../../../../constants/routes";
import classes from "./styles.module.scss";
import clsx from "clsx";

type WebkitPatchedCSSProperties = React.CSSProperties & {
  WebkitTextFillColor?: string;
  WebkitAppearance?: React.CSSProperties["appearance"];
};

const codeInputStyle: WebkitPatchedCSSProperties = {
  width: "55px",
  height: "80px",
  backgroundColor: "var(--yna-color-white)",
  border: "0.75px solid var(--yna-color-border)",
  borderRadius: "4px",
  fontSize: "55px",
  lineHeight: "80px",
  textAlign: "center",
  paddingLeft: 0,
  paddingRight: 0,
  boxSizing: "border-box",
  marginRight: "25px",
  color: "var(--yna-color-main)",
  WebkitTextFillColor: "var(--yna-color-main)",
  WebkitAppearance: "none",
  appearance: "none",
  opacity: 1,
};

const codeInputStyleInvalid: WebkitPatchedCSSProperties = {
  border: "0.75px solid var(--yna-color-red)",
  color: "var(--yna-color-main)",
  WebkitTextFillColor: "var(--yna-color-main)",
};

export type VerifyCodeFormProps = {
  type: VerifyCodeTypes;
};

export const VerifyCodeForm = (props: VerifyCodeFormProps) => {
  const { api, store } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [verifyCodeExpired, setVerifyCodeExpired] = useState(false);
  const [wrongCodeError, setWrongCodeError] = useState(false);

  const formik = useFormik({
    initialValues: {
      token: store.authStore.getVerificationToken()?.token ?? "",
      code: "",
    },
    validationSchema: VerifyCodeSchema(t),
    onSubmit: async (values) => {
      setVerifyCodeExpired(false);
      setWrongCodeError(false);
      const result = await api.authApi.verifyCode({
        token: values.token,
        code: values.code,
        type: props.type,
      });
      if (result.success) {
        redirect();
      } else {
        if (result.errors === "verify_code_expired") {
          setVerifyCodeExpired(true);
        } else if (result.errors === "wrong_verify_code") {
          setWrongCodeError(true);
        }
      }
    },
  });

  const redirect = () => {
    if (props.type === VerifyCodeTypes.registration) {
      store.authStore.clearVerificationToken();
      if (params.has("redirect")) {
        const redirectUrl = params.get("redirect");
        navigate(redirectUrl || ROUTES.DASHBOARD);
      } else {
        navigate(ROUTES.DASHBOARD);
      }
      return;
    }

    if (props.type === VerifyCodeTypes.restorePassword) {
      navigate(
        `${ROUTES.LOGIN}${ROUTES.NEW_PASSWORD}?token=${btoa(
          store.authStore.getVerificationToken()?.token ?? ""
        )}`
      );
      return;
    }

    navigate(ROUTES.LOGIN, { replace: true });
  };

  const onRetryCodeHandler = async () => {
    await api.authApi.resendVerifyCode();
  };

  return (
    <div className={classes.wrapper}>
      <Typography variant="h4" className={classes.title}>
        {t("verifyCodeTitle")}
      </Typography>
      <Typography variant="h5" className={classes.subtitle}>
        {t("verifyCodeSubtitle")}
      </Typography>

      <form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
        <Stack spacing={113}>
          <Grid container gap={37}>
            <Grid item xs={12} className={classes.codeInputContainer}>
              <ReactCodeInput
                type="text"
                fields={4}
                name="code"
                inputMode={"tel"}
                inputStyle={codeInputStyle}
                inputStyleInvalid={codeInputStyleInvalid}
                className={clsx(
                  classes.codeInputWrapper,
                  (verifyCodeExpired || wrongCodeError) && classes.codeInputWrapperError
                )}
                 value={formik.values.code}
                 onChange={(value) => {
                   formik.setFieldValue("code", value);
                   if (wrongCodeError) setWrongCodeError(false);
                 }}
              />
                {formik.errors.code && formik.submitCount > 0 ? (
                  <div className={classes.errorMessage}>{formik.errors.code}</div>
                ) : null}
                {wrongCodeError ? (
                  <div className={classes.errorMessage}>{t("wrongVerifyCode")}</div>
                ) : null}
            </Grid>

            <Stack spacing={17} className={classes.submitButtonContainer}>
              <Button
                buttonSize="lg"
                type="submit"
                disabled={formik.values.code?.length !== 4}
                fullWidth
              >
                {t("verifyCodeBtn")}
              </Button>

              <Grid
                container
                display={"flex"}
                alignContent={"center"}
                justifyContent={"center"}
                className={classes.retryWrapper}
              >
                <Typography component="span" className={classes.retryLinkText}>{t("notReceivedCode")} &nbsp;</Typography>
                <RetryCodeLinkButton
                  onRetryCodeHandler={onRetryCodeHandler}
                  className={classes.retryLink}
                  disabledClassName={classes.retryLinkDisabled}
                  title={t("retryCodeLinkTitle")}
                />
              </Grid>
            </Stack>
          </Grid>
          {verifyCodeExpired && (
            <Grid
              container
              display={"flex"}
              alignContent={"center"}
              justifyContent={"center"}
              className={classes.expiredWrapper}
            >
              <Typography className={classes.expiredText} component="span">
                {t("verifyCodeExpired")}
              </Typography>
            </Grid>
          )}

          <Button
            buttonSize="lg"
            buttonType="secondary"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              navigate(-1);
            }}
          >
            Отмена
          </Button>
        </Stack>
      </form>
    </div>
  );
};
